import { useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress, InputAdornment, MenuItem, TextField } from "@mui/material";
import {
  GovItemDetail,
  createItem,
  getItems,
  governanceErrorMessage,
} from "../../../../utils/icGovernance";
import { CreateDialogShell, optional } from "./dialogParts";

/** The bits of a release the select needs (a GovRelease or a ReleaseRef). */
export type ReleaseOption = {
  id: string;
  displayId: string | null;
  version: string | null;
  title: string;
};

export const releaseOptionLabel = (r: ReleaseOption) =>
  `${r.displayId || "Release"}${r.version ? ` v${r.version}` : ""} — ${r.title || "(untitled)"}`;

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (item: GovItemDetail) => void;
  /** Preselected release (the UAT register's `?release=`). */
  defaultRelease?: ReleaseOption | null;
};

const NewUatTestDialog = ({ open, onClose, onCreated, defaultRelease }: Props) => {
  const [releases, setReleases] = useState<ReleaseOption[]>([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [releasesError, setReleasesError] = useState<string | null>(null);
  const [releaseId, setReleaseId] = useState("");
  const [requirement, setRequirement] = useState("");
  const [scenario, setScenario] = useState("");
  const [preconditions, setPreconditions] = useState("");
  const [expected, setExpected] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savingRef = useRef(false);

  const defaultReleaseId = defaultRelease?.id ?? "";

  // A fresh form (and a fresh release list) every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setRequirement("");
    setScenario("");
    setPreconditions("");
    setExpected("");
    setError(null);
    setSaving(false);
    savingRef.current = false;

    let alive = true;
    setReleasesLoading(true);
    setReleasesError(null);
    getItems({ kind: "RELEASE", sort: "-createdAt" })
      .then((page) => {
        if (alive) setReleases((page.items ?? []) as ReleaseOption[]);
      })
      .catch((err) => {
        if (alive) setReleasesError(governanceErrorMessage(err));
      })
      .finally(() => {
        if (alive) setReleasesLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open]);

  useEffect(() => {
    if (open) setReleaseId(defaultReleaseId);
  }, [open, defaultReleaseId]);

  // The preselected release stays choosable even when it is older than the
  // first page of the releases register.
  const options = useMemo(() => {
    if (defaultRelease && !releases.some((r) => r.id === defaultRelease.id)) {
      return [defaultRelease, ...releases];
    }
    return releases;
  }, [releases, defaultRelease]);

  const canSubmit = !!releaseId && requirement.trim().length > 0 && scenario.trim().length > 0;

  const submit = async () => {
    if (!canSubmit || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const item = await createItem({
        kind: "UAT_TEST",
        releaseId,
        requirement: requirement.trim(),
        scenario: scenario.trim(),
        preconditions: optional(preconditions),
        expectedResult: optional(expected),
      });
      onCreated(item);
    } catch (err) {
      setError(governanceErrorMessage(err));
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <CreateDialogShell
      open={open}
      titleId="new-uat-test-title"
      title="New UAT test"
      saving={saving}
      error={error ?? releasesError}
      canSubmit={canSubmit}
      submitLabel="Add test"
      onClose={onClose}
      onSubmit={submit}
    >
      <TextField
        select
        label="Release"
        value={options.some((r) => r.id === releaseId) ? releaseId : ""}
        onChange={(e) => setReleaseId(e.target.value)}
        required
        fullWidth
        disabled={saving || (releasesLoading && options.length === 0)}
        helperText={!releasesLoading && options.length === 0 ? "No releases in this project yet." : undefined}
        InputProps={
          releasesLoading
            ? {
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: 3 }}>
                    <CircularProgress size={16} />
                  </InputAdornment>
                ),
              }
            : undefined
        }
      >
        {options.map((r) => (
          <MenuItem key={r.id} value={r.id}>
            {releaseOptionLabel(r)}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Requirement / feature"
        value={requirement}
        onChange={(e) => setRequirement(e.target.value)}
        required
        autoFocus={!!defaultReleaseId}
        fullWidth
        disabled={saving}
      />
      <TextField
        label="Test scenario"
        placeholder="What the tester does, step by step"
        value={scenario}
        onChange={(e) => setScenario(e.target.value)}
        required
        multiline
        minRows={3}
        fullWidth
        disabled={saving}
      />
      <TextField
        label="Preconditions / test data"
        value={preconditions}
        onChange={(e) => setPreconditions(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        disabled={saving}
      />
      <TextField
        label="Expected result"
        value={expected}
        onChange={(e) => setExpected(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        disabled={saving}
      />
    </CreateDialogShell>
  );
};

export default NewUatTestDialog;
